import React, { useState } from 'react';
import { History, X, RotateCcw, Clock, Check, Info, ChevronRight } from 'lucide-react';

interface VersionItem {
    version: string;
    id?: string;
    createdDate?: string;
    modifiedDate?: string;
}

interface VersionHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    versions: VersionItem[];
    currentVersion: string;
    onRevert: (version: string) => Promise<void>;
    onPreview?: (version: string) => Promise<void>;
    title?: string;
}

const VersionHistoryDrawer: React.FC<VersionHistoryDrawerProps> = ({
    isOpen,
    onClose,
    versions,
    currentVersion,
    onRevert,
    onPreview,
    title = 'Version History'
}) => {
    const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
    const [isReverting, setIsReverting] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);

    const handleRevert = async () => {
        if (!selectedVersion || selectedVersion === currentVersion) return;

        setIsReverting(true);
        try {
            await onRevert(selectedVersion);
        } catch (error) {
            console.error('Failed to restore:', error);
        } finally {
            setIsReverting(false);
        }
    };

    const handlePreview = async (version: string) => {
        if (!onPreview) return;

        setSelectedVersion(version);
        setIsPreviewing(true);
        try {
            await onPreview(version);
        } catch (error) {
            console.error('Failed to preview:', error);
        } finally {
            setIsPreviewing(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return '';
        }
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/20 z-40 transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Drawer */}
            <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
                        <div className="flex items-center gap-2">
                            <History size={18} className="text-white" />
                            <h3 className="text-base font-semibold text-white">{title}</h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Info Banner */}
                    <div className="mx-3 mt-3 p-2.5 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-2">
                        <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-700 leading-relaxed">
                            Click on a version to preview it. Use <span className="font-semibold">Restore</span> to create a new version from the selected one.
                        </p>
                    </div>

                    {/* Version List */}
                    <div className="flex-1 overflow-y-auto p-3">
                        {versions.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">
                                <Clock size={40} className="mx-auto mb-3 text-gray-300" />
                                <p className="text-sm">No version history available</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {versions.map((version) => {
                                    const isCurrent = version.version === currentVersion;
                                    const isSelected = version.version === selectedVersion;
                                    const dateStr = formatDate(version.createdDate || version.modifiedDate);

                                    return (
                                        <div
                                            key={version.version}
                                            className={`relative rounded-lg border-2 transition-all ${isCurrent
                                                ? 'bg-green-50 border-green-300'
                                                : isSelected
                                                    ? 'bg-indigo-50 border-indigo-400'
                                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                                }`}
                                        >
                                            <button
                                                onClick={() => {
                                                    if (!isCurrent && onPreview) {
                                                        handlePreview(version.version);
                                                    } else if (!isCurrent) {
                                                        setSelectedVersion(version.version);
                                                    }
                                                }}
                                                disabled={isCurrent || isPreviewing}
                                                className="w-full flex items-center gap-3 p-3 text-left disabled:cursor-default"
                                            >
                                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-semibold text-sm ${isCurrent
                                                    ? 'bg-green-200 text-green-800'
                                                    : isSelected
                                                        ? 'bg-indigo-200 text-indigo-800'
                                                        : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    v{version.version}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-gray-900 text-sm">Version {version.version}</span>
                                                        {isCurrent && (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-200 text-green-800 text-[10px] font-semibold rounded-full">
                                                                <Check size={10} /> Current
                                                            </span>
                                                        )}
                                                    </div>
                                                    {dateStr && (
                                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                                            {dateStr}
                                                        </p>
                                                    )}
                                                </div>
                                                {!isCurrent && (
                                                    <ChevronRight size={16} className="text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleRevert}
                                disabled={!selectedVersion || selectedVersion === currentVersion || isReverting}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RotateCcw size={14} />
                                {isReverting ? 'Restoring...' : 'Restore'}
                            </button>
                        </div>
                        {selectedVersion && selectedVersion !== currentVersion && (
                            <p className="mt-2 text-[11px] text-center text-gray-500">
                                Restoring will load v{selectedVersion} content. Save to create a new version.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default VersionHistoryDrawer;

// Re-export for backward compatibility with existing imports
export { VersionHistoryDrawer as VersionHistoryModal };

